import React, { useState, useEffect, ForwardedRef } from 'react'
import Image from 'next/image'
import styles from './styles.module.scss'
import { RatingTier } from './components/RatingTier'
import { useAppIntl } from 'src/i18n'

interface RatingAttributes {
  content: string
  createdAt: string
  fourToSix: string
  name: string
  oneToThree: string
  publishedAt: string
  sevenToNine: string
  ten: string
  updatedAt: string
}

interface RatingAttribute {
  ratingAttribute: any
  attributes: RatingAttributes
  id: number
}

interface RatingData {
  attributes: RatingAttribute
}

interface Rating {
  data: RatingData[]
}

interface DetailedReviewItem {
  ratings: Rating
}

interface HowWeRateProps {
  detailedReviewItems: DetailedReviewItem[]
}
// Use ForwardRefRenderFunction to correctly type props and ref
const HowWeRate: React.ForwardRefRenderFunction<
  HTMLDivElement,
  HowWeRateProps
> = (props, ref) => {
  const intl = useAppIntl()
  const { detailedReviewItems } = props
  const [openRatingTiers, setOpenRatingTiers] = useState<number[]>([])
  const [relevantRatingAttributes, setRelevantRatingAttributes] = useState([])

  const scrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    })
  }

  const toggleRatingTiers = (index: number) => {
    setOpenRatingTiers(prevOpenIndexes => {
      if (prevOpenIndexes.includes(index)) {
        return prevOpenIndexes.filter(i => i !== index)
      } else {
        return [...prevOpenIndexes, index]
      }
    })
  }

  useEffect(() => {
    const newRelevantRatingAttributes = detailedReviewItems.reduce(
      (acc, review) => {
        review.ratings.data.forEach(rating => {
          const attribute = rating.attributes.ratingAttribute.data.attributes
          const existingAttributeIndex = acc.findIndex(
            attr => attr.attributes.name === attribute.name
          )

          if (existingAttributeIndex === -1) {
            acc.push({ attributes: attribute })
          }
        })
        return acc
      },
      []
    )
    setRelevantRatingAttributes(newRelevantRatingAttributes)
  }, [detailedReviewItems])
  return (
    <div className={styles.main} ref={ref} id='howWeRate'>
      <header className={styles.header}>
        <h1 className={styles.title}>{intl.t('review.howWeRate.title')}</h1>
        <button className={styles.navButton} onClick={scrollTop}>
          {intl.t('review.howWeRate.backToTop')}
          <Image
            className={styles.arrow}
            src='/assets/icons/icon-white-arrow-up.svg'
            alt='Arrow Up'
            width={24}
            height={24}
          />
        </button>
      </header>
      {relevantRatingAttributes?.map((rating, index) => (
        <div key={index} className={styles.container}>
          <div className={styles.ratingCategory}>
            <h3>{rating.attributes.name}</h3>
            <Image
              className={styles.image}
              src={
                openRatingTiers.includes(index)
                  ? '/assets/icons/icon-white-minus.svg'
                  : '/assets/icons/icon-white-plus.svg'
              }
              alt='Arrow Down'
              width={24}
              height={24}
              onClick={() => toggleRatingTiers(index)}
            />
          </div>
          <div
            className={`${styles.ratingTierContainer} ${
              openRatingTiers.includes(index) ? styles.open : styles.closed
            }`}
          >
            <RatingTier
              backgroundColor='#1D1D1D'
              attributes={rating.attributes}
            />
          </div>
        </div>
      ))}
    </div>
  )
}

export default React.forwardRef(HowWeRate)
